
import PropTypes from "prop-types";
import { defineMessage } from "react-intl";
import { useIntl } from "react-intl";

export const messageFilter = (item) => {
    return messageTest(item?.message);
};
export const messageTest = (item) => {
    return Boolean(item?.id) &&
        typeof item?.defaultMessage === "string" &&
        item.defaultMessage.length > 0;
}

const fallbackErrorMessage = defineMessage({
    id: "safeFormattedMessage.genericError",
    defaultMessage: "An error occurred while displaying this message. If this continues, please contact support.",
    description: "Generic error message displayed when a translated error fails to render the intended message."
});

export const safelyFormatMessage = ({intl,message}) => {
    const fallbackError = () => intl.formatMessage(fallbackErrorMessage);
    
    if (typeof message !== "object") {
        console.error("Cannot display message: bad message object", { message });
        return fallbackError();
    }

    const {id, defaultMessage, values} = message;

    if (!messageTest({ id, defaultMessage })) {
        console.error("Invalid message object, missing id or defaultMessage", { message });
        return fallbackError();
    }
    
    try {
        return intl.formatMessage(message, values); 
    } catch (error) {
        console.error("Error formatting message", { error });
    }
    console.warn("Cannot display message:", { message });
    return fallbackError();
}

// This component should be used to shim FormattedMessage to avoid runtime errors due to missing id or defaultMessage. 
// This component does not need to be used in cases where the defaultMessage and id are guaranteed to be string literals.
export const SafeFormattedMessage = (props) => {
    const intl = useIntl();
    const fallbackError = () => intl.formatMessage(fallbackErrorMessage);

    if (!messageTest(props)) {
        console.error("Cannot display message: Missing id or defaultMessage", { id, defaultMessage, description, remainingProps });
        return fallbackError();
    }

    const {
        id,
        defaultMessage,
        values,
        description,
        ...remainingProps
    } = props;

    if (id && typeof defaultMessage === "string" && defaultMessage.length > 0) {
        // This is to disable the lint rule for enforcing defaultMessage being a string literal
        // defaultMessage should always be a literal string, but this is intentially wrapping its functionality

        try {
            // eslint-disable-next-line formatjs/enforce-default-message
            return intl.formatMessage({ id, defaultMessage, description }, values);
        } catch (error) {
            console.error("Error formatting message", { error });
        }
        console.warn("Cannot display message:", { message: props });
        return fallbackError();
    }
};

SafeFormattedMessage.propTypes = {
    id: PropTypes.string.isRequired,
    defaultMessage: PropTypes.string.isRequired,
    description: PropTypes.string,
    values: PropTypes.object,
};